import { BIOMES, type BiomeId } from '../content/biomes';
import { BREAK_TIER, type TileId } from '../content/tiles';
import { Tile, isSolid, type ExtraCell } from '../sim/grid';
import type { PickupSpawn, SimLevel } from '../sim/level';
import { hashString32 } from '../sim/rng';

/**
 * Secret yards (owner, 2026-10-05): a few lawns hide a little garden beyond their fence, through
 * a gap plugged by something only some mowers can smash (a bench against the back fence, a shrub
 * or bird bath or pile of sticks in a side fence, a gnome at the front). Breaking through finds a
 * yard that is already perfectly kept (nothing to mow), full of bonus coins, with an escaped rare
 * find or a hoard of coins on a pedestal. Never needed to finish the lawn, never part of a star.
 *
 * Three lawns in each zone from the Park on, never a boss. All of it is worked out from the lawn
 * itself, so the baked lawns, their plans and their pars stay exactly as they are.
 */

export type YardSide = 'top' | 'bottom' | 'left' | 'right';

/** What can plug a gate (a breakable lawn item, or sticks for the mowers that plow them). */
export type GateItem = Extract<
  TileId,
  | 'bench'
  | 'bin'
  | 'shrub'
  | 'birdBath'
  | 'sticks'
  | 'gnome'
  | 'cone'
  | 'cornerFlag'
  | 'ballWasher'
  | 'urn'
>;

/**
 * Where each zone may put a gate, and what plugs it, chosen to look at home: a bench stands
 * against the back (top) fence, bushes and bird baths and sticks in the side fences, a gnome by the
 * front.
 */
const GATES: Partial<Record<BiomeId, readonly { side: YardSide; item: GateItem }[]>> = {
  park: [
    { side: 'top', item: 'bench' },
    { side: 'left', item: 'birdBath' },
    { side: 'right', item: 'shrub' },
    { side: 'bottom', item: 'gnome' },
  ],
  farm: [
    { side: 'top', item: 'bench' },
    { side: 'left', item: 'sticks' },
    { side: 'right', item: 'sticks' },
    { side: 'bottom', item: 'gnome' },
  ],
  golf: [
    { side: 'top', item: 'bench' },
    { side: 'left', item: 'ballWasher' },
    { side: 'right', item: 'shrub' },
    { side: 'bottom', item: 'bin' },
  ],
  cemetery: [
    { side: 'top', item: 'bench' },
    { side: 'left', item: 'sticks' },
    { side: 'right', item: 'shrub' },
    { side: 'bottom', item: 'sticks' },
  ],
  estate: [
    { side: 'top', item: 'bench' },
    { side: 'left', item: 'shrub' },
    { side: 'right', item: 'birdBath' },
    { side: 'bottom', item: 'urn' },
  ],
  stadium: [
    { side: 'top', item: 'bin' },
    { side: 'left', item: 'cone' },
    { side: 'right', item: 'cornerFlag' },
    { side: 'bottom', item: 'cone' },
  ],
  meadow: [
    { side: 'top', item: 'bench' },
    { side: 'left', item: 'sticks' },
    { side: 'right', item: 'shrub' },
    { side: 'bottom', item: 'sticks' },
  ],
};

/** Across the gate's side and away from the lawn, in tiles. */
export const YARD_ACROSS = 5;
export const YARD_DEPTH = 4;

export interface SecretYard {
  side: YardSide;
  item: GateItem;
  /** The gate: in the fence line, just beyond the lawn's edge tile in front of it. */
  gate: { x: number; y: number };
  /** The yard beyond it (its cells lie outside the lawn). */
  area: { x: number; y: number; w: number; h: number };
  /** A fountain in the middle (solid). */
  fountain: { x: number; y: number };
  /** The pedestal at the far end (a hoard or a rare find). */
  pedestal: { x: number; y: number };
  /** Where coins go, nearest the gate first (a revisit takes only the first few). */
  coinSpots: readonly { x: number; y: number }[];
}

/** Coins in a yard: the first haul, and on later visits. */
export const FIRST_HAUL = 18;
export const REVISIT_COINS = 4;
/** The pedestal's hoard when no rare find is waiting. */
export const HOARD_VALUE = 25;

// ── Which lawns ──────────────────────────────────────────────────────────────────────────────

/**
 * The campaign's secret yards, by lawn number: one in each third of each zone from the Park on
 * (never its boss), on a lawn where one of the zone's gates fits, each of a zone's three behind a
 * different kind of gate where it can be. Needs the lawns' sim levels to check the fit.
 */
export function planSecretYards(
  simFor: (lawn: number) => SimLevel | undefined,
): Map<number, SecretYard> {
  const out = new Map<number, SecretYard>();
  for (const zone of BIOMES.slice(1)) {
    const used = new Set<GateItem>();
    const [a, b] = zone.levels;
    const thirds = [
      [a, a + 5],
      [a + 6, a + 12],
      [a + 13, b - 1],
    ];
    for (const [lo, hi] of thirds) {
      const lawns: number[] = [];
      for (let n = lo!; n <= hi!; n++) {
        const sim = simFor(n);
        if (sim && placeYard(sim, zone.id, n)) lawns.push(n);
      }
      if (lawns.length === 0) continue;
      const n = lawns[hashString32(`yard-lawn:${zone.id}:${lo}`) % lawns.length]!;
      const yard = placeYard(simFor(n)!, zone.id, n, used)!;
      used.add(yard.item);
      out.set(n, yard);
    }
  }
  return out;
}

// ── Where on the lawn ────────────────────────────────────────────────────────────────────────

/** A lawn edge tile the mower can stand on in front of a gate. */
function openEdge(sim: SimLevel, x: number, y: number): boolean {
  const t = sim.tiles[y * sim.width + x] as Tile;
  return !isSolid(t) && t !== Tile.Brush && t !== Tile.Hive;
}

/**
 * Lays out the zone's yard for a lawn: picks a gate (side and item) and a spot along that side
 * near its middle whose edge tile is open, and the yard beyond it. Null if none fits.
 */
export function placeYard(
  sim: SimLevel,
  zone: BiomeId,
  lawn: number,
  avoid: ReadonlySet<GateItem> = new Set(),
): SecretYard | null {
  const menu = GATES[zone];
  if (!menu) return null;
  const h = hashString32(`yard:${lawn}`);
  // The zone's gates in an order of the lawn's own, so lawns in a zone vary; gates its other
  // yards already use go last.
  const turned = menu.map((_, i) => menu[(i + h) % menu.length]!);
  const order = [
    ...turned.filter((g) => !avoid.has(g.item)),
    ...turned.filter((g) => avoid.has(g.item)),
  ];
  for (const { side, item } of order) {
    const along = side === 'top' || side === 'bottom' ? sim.width : sim.height;
    // Spots along the side, nearest the middle first, keeping clear of the corners.
    const spots: number[] = [];
    for (let p = 2; p <= along - 3; p++) spots.push(p);
    spots.sort((p, q) => Math.abs(p - (along - 1) / 2) - Math.abs(q - (along - 1) / 2) || p - q);
    for (const p of spots.slice(0, 5)) {
      const edge =
        side === 'top'
          ? { x: p, y: 0 }
          : side === 'bottom'
            ? { x: p, y: sim.height - 1 }
            : side === 'left'
              ? { x: 0, y: p }
              : { x: sim.width - 1, y: p };
      if (!openEdge(sim, edge.x, edge.y)) continue;
      return layout(side, item, p, sim.width, sim.height);
    }
  }
  return null;
}

/** The gate, yard, fountain, pedestal and coin spots for a gate at `p` along `side`. */
function layout(side: YardSide, item: GateItem, p: number, w: number, h: number): SecretYard {
  // Yard-local (u across, v away from the gate, 1 = the row next to it) to lawn coordinates.
  const half = Math.floor(YARD_ACROSS / 2);
  const at = (u: number, v: number): { x: number; y: number } => {
    const across = p - half + u;
    switch (side) {
      case 'top':
        return { x: across, y: -1 - v };
      case 'bottom':
        return { x: across, y: h + v };
      case 'left':
        return { x: -1 - v, y: across };
      case 'right':
        return { x: w + v, y: across };
    }
  };
  const gate = at(half, 0);
  const corners = [at(0, 1), at(YARD_ACROSS - 1, YARD_DEPTH)];
  const area = {
    x: Math.min(corners[0]!.x, corners[1]!.x),
    y: Math.min(corners[0]!.y, corners[1]!.y),
    w: side === 'top' || side === 'bottom' ? YARD_ACROSS : YARD_DEPTH,
    h: side === 'top' || side === 'bottom' ? YARD_DEPTH : YARD_ACROSS,
  };
  // Two open rows before the fountain, so a mower coming through the gate has room to turn.
  const fountain = at(half, 3);
  const pedestal = at(half, YARD_DEPTH);
  // Coins on every other cell, nearest the gate first (ring round the fountain).
  const spots: { x: number; y: number }[] = [];
  for (let v = 1; v <= YARD_DEPTH; v++) {
    for (let u = 0; u < YARD_ACROSS; u++) {
      const c = at(u, v);
      const isFountain = c.x === fountain.x && c.y === fountain.y;
      const isPedestal = c.x === pedestal.x && c.y === pedestal.y;
      if (!isFountain && !isPedestal) spots.push(c);
    }
  }
  return { side, item, gate, area, fountain, pedestal, coinSpots: spots };
}

// ── Into the sim level ───────────────────────────────────────────────────────────────────────

/** What the yard holds this time (the save decides: first visit or not, a rare find waiting). */
export interface YardContents {
  coins: number;
  /** The pedestal: a hoard, a rare find, or nothing. */
  pedestal: 'hoard' | 'rare' | null;
}

/** The yard's cells for the sim: the gate (breakable, or sticks), the yard, the fountain. */
export function yardCells(yard: SecretYard): ExtraCell[] {
  const tier = BREAK_TIER[yard.item];
  const cells: ExtraCell[] = [
    yard.item === 'sticks'
      ? { x: yard.gate.x, y: yard.gate.y, tile: Tile.Brush }
      : {
          x: yard.gate.x,
          y: yard.gate.y,
          tile: yard.item === 'shrub' ? Tile.Hedge : Tile.Obstacle,
          toughness: tier ?? 0,
        },
  ];
  const { x, y, w, h } = yard.area;
  for (let cy = y; cy < y + h; cy++) {
    for (let cx = x; cx < x + w; cx++) {
      const fountain = cx === yard.fountain.x && cy === yard.fountain.y;
      cells.push({ x: cx, y: cy, tile: fountain ? Tile.Obstacle : Tile.Ground });
    }
  }
  return cells;
}

/** A lawn's sim level with its secret yard added (its own tiles, start and pickups untouched). */
export function withSecretYard(sim: SimLevel, yard: SecretYard, contents: YardContents): SimLevel {
  const pickups: PickupSpawn[] = [...(sim.pickups ?? [])];
  for (const c of yard.coinSpots.slice(0, contents.coins)) {
    pickups.push({ kind: 'treasure', x: c.x, y: c.y });
  }
  if (contents.pedestal) {
    pickups.push({ kind: contents.pedestal, x: yard.pedestal.x, y: yard.pedestal.y });
  }
  return { ...sim, extras: [...(sim.extras ?? []), ...yardCells(yard)], pickups };
}
